// src/utils/getAutomationsApi.ts
import { BASE_URL } from "../../utils/constants";

interface AutomationTrigger {
    type: string;
    conditions?: {
        op: 'AND' | 'OR';
        conditions: Array<{
            entity: string;
            field: string;
            operator: string;
        }>;
    };
}

interface AutomationGraphNode {
    id: string;
    type: string;
    cfg: Record<string, any>;
    edges: {
        next?: string;
    };
}

interface AutomationGraph {
    nodes: Record<string, AutomationGraphNode>;
    nextByDefault?: string | null;
}

interface EntryPolicy {
    reentry: string;
    windowDays: number | null;
    window: boolean;
    once: boolean;
    multiple: boolean;
}

interface ValidationResult {
    errors: string[];
    isValid: boolean;
    warnings: string[];
}

interface Automation {
    id: string;
    tenantId: string;
    name: string;
    description: string | null;
    status: 'DRAFT' | 'PUBLISHED' | 'PAUSED' | 'ARCHIVED';
    revision: number;
    entryPolicy: EntryPolicy;
    trigger: AutomationTrigger;
    graph: AutomationGraph;
    createdAt: string;
    createdBy: string;
    updatedAt: string;
    updatedBy: string;
    publishedAt: string | null;
    publishedBy: string | null;
    pausedAt: string | null;
    pauseReason: string | null;
    archivedAt: string | null;
    archiveReason: string | null;
    resumedAt: string | null;
    resumedBy: string | null;
    clonedFrom: string | null;
    validation: ValidationResult;
    metadata: Record<string, any> | null;
    active: boolean;
    draft: boolean;
    paused: boolean;
    archived: boolean;
}

interface GetAutomationsResponse {
    items: Automation[];
}

// Main function to get automations via API
export const getAutomations = async (
    token: string,
    tenantId: string,
    userId: string,
    baseUrl: string = BASE_URL
): Promise<GetAutomationsResponse> => {

    // Make the API call
    const url = `${baseUrl}/api/v1/automations`;

    try {
        const response = await fetch(url, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'X-Tenant-Id': tenantId,
                'X-User-Id': userId || '',
                'Authorization': `Bearer ${token}`,
            },
        });

        if (!response.ok) {
            let errorMessage = `Failed to fetch automations (${response.status})`;
            try {
                const errorData = await response.json();
                if (errorData.message) {
                    errorMessage = errorData.message;
                } else if (errorData.error) {
                    errorMessage = errorData.error;
                }
            } catch {
                errorMessage = response.statusText || errorMessage;
            }
            throw new Error(errorMessage);
        }

        const data = await response.json();

        // Reverse the items array to show newest first (most recent createdAt)
        if (data && data.items && Array.isArray(data.items)) {
            // Sort by createdAt in descending order (newest first)
            data.items.sort((a: Automation, b: Automation) => {
                const dateA = new Date(a.createdAt).getTime();
                const dateB = new Date(b.createdAt).getTime();
                return dateB - dateA; // Descending order (newest first)
            });
        }

        return data;

    } catch (error) {
        // console.error('❌ Error fetching automations:', error);
        throw error;
    }
};

// Helper function to map API status to display status
export const mapStatusToDisplay = (status: string): string => {
    switch (status) {
        case 'PUBLISHED':
            return 'Active';
        case 'DRAFT':
            return 'Draft';
        case 'PAUSED':
            return 'Paused';
        case 'ARCHIVED':
            return 'Archived';
        default:
            return status || 'Draft';
    }
};

// Helper function to get status color for badge
export const getStatusColor = (status: string): string => {
    switch (status) {
        case 'PUBLISHED':
            return 'bg-green-100 text-green-600';
        case 'DRAFT':
            return 'bg-red-100 text-red-600';
        case 'PAUSED':
            return 'bg-yellow-100 text-yellow-600';
        case 'ARCHIVED':
            return 'bg-gray-100 text-gray-600';
        default:
            return 'bg-gray-100 text-gray-600';
    }
};

// Helper function to format date
export const formatDate = (dateString: string): string => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
    });
};

// Helper function to get trigger display name
export const getTriggerDisplayName = (triggerType: string): string => {
    switch (triggerType) {
        case 'FormSubmission':
            return 'Form Submission';
        case 'NewSubscriber':
            return 'New Subscriber';
        case 'EmailOpened':
            return 'Email Opened';
        case 'LinkClicked':
            return 'Link Clicked';
        case 'Scheduled':
            return 'Scheduled Date/Time';
        default:
            return triggerType || 'Unknown';
    }
};